<template>
  <div
    class="modal fade"
    id="modalCollectPayment"
    ref="modal"
    data-bs-backdrop="static"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content border-0 shadow-lg" style="border-radius: 16px;">
        <div class="modal-header border-0 pb-2">
          <h5 class="modal-title fw-bold">
            <i class="fa-solid fa-money-bill-wave me-2 text-success"/>
            {{ $t('Collect Payment') }}
            <span class="text-muted ms-1">{{ bill.bill_no }}</span>
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"/>
        </div>

        <div class="modal-body px-4 pb-4">
          <div class="card bg-light border-0 rounded-3 mb-3">
            <div class="card-body p-3 d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted small">{{ $t('Remaining') }}</div>
                <div class="fs-4 fw-bold text-danger">
                  {{ $storeIndex.formatThaiCurrency(remainingAmount) }}
                </div>
                <div class="text-muted small mt-1">
                  {{ $t('Total') }}: {{ $storeIndex.formatThaiCurrency(billTotal) }}
                  <span v-if="paidAmount > 0">
                    · {{ $t('Paid') }}: {{ $storeIndex.formatThaiCurrency(paidAmount) }}
                  </span>
                </div>
              </div>
              <i class="fa-solid fa-circle-exclamation fa-2x text-warning"/>
            </div>
          </div>

          <div v-if="paymentMethods.length === 0" class="alert alert-warning small">
            <i class="fa-solid fa-circle-exclamation me-1"/>
            {{ $t('No payment methods available for this branch.') }}
          </div>

          <template v-else>
            <div
              v-for="(line, idx) in lines"
              :key="idx"
              class="border rounded-3 p-3 mb-2"
            >
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span class="fw-semibold small">
                  {{ $t('Payment') }} #{{ idx + 1 }}
                </span>
                <button
                  v-if="lines.length > 1"
                  type="button"
                  class="btn btn-sm btn-link text-danger p-0"
                  @click="removeLine(idx)"
                >
                  <i class="fa-solid fa-trash"/>
                </button>
              </div>

              <div class="row g-2">
                <div class="col-12">
                  <label class="form-label small mb-1">{{ $t('Payment Method') }}</label>
                  <div class="row g-2">
                    <div
                      v-for="m in paymentMethods"
                      :key="m.id"
                      class="col-6 col-md-4"
                    >
                      <button
                        type="button"
                        class="btn w-100 py-2 fw-semibold rounded-3 small text-start method-btn"
                        :class="line.id === m.id
                          ? ('btn-' + methodColor(m.type) + ' text-white')
                          : ('btn-outline-' + methodColor(m.type))"
                        @click="setMethod(idx, m)"
                      >
                        <div class="d-flex align-items-center">
                          <i :class="methodIcon(m.type)" class="me-2 fs-5"/>
                          <div class="text-truncate">
                            <div class="text-truncate">{{ m.name }}</div>
                            <div v-if="m.bank_name" class="text-truncate small opacity-75">
                              {{ m.bank_name }}
                            </div>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- PromptPay QR — shown when selected method has promptpay_no -->
                <div v-if="line.methodInfo && line.methodInfo.promptpay_no" class="col-12">
                  <div
                    class="card border-0 rounded-3"
                    style="background: linear-gradient(135deg, #002d5f 0%, #0056b3 100%);"
                  >
                    <div class="card-body p-3 text-center text-white">
                      <div class="fw-bold mb-2" style="letter-spacing: 1px;">PromptPay</div>
                      <div class="bg-white rounded-3 p-2 d-inline-block">
                        <div v-if="qrUrls[idx]" class="position-relative" style="width: 200px; height: 200px;">
                          <img
                            :src="qrUrls[idx]"
                            alt="PromptPay QR"
                            class="img-fluid"
                            style="width: 200px; height: 200px;"
                          >
                          <img
                            :src="promptPayIcon"
                            alt="PromptPay"
                            class="position-absolute top-50 start-50 translate-middle"
                            style="width: 40px; height: 40px; object-fit: contain;"
                          >
                        </div>
                        <div
                          v-else
                          class="text-muted small d-flex align-items-center justify-content-center px-2 text-center"
                          style="width: 200px; height: 200px;"
                        >
                          {{ $t('Enter amount to generate QR') }}
                        </div>
                      </div>
                      <div class="small mt-2 opacity-75">
                        <i class="fa-solid fa-phone me-1"/>
                        {{ formatPromptPayId(line.methodInfo.promptpay_no) }}
                      </div>
                      <div v-if="line.methodInfo.account_name" class="small opacity-75">
                        <i class="fa-solid fa-user me-1"/>
                        {{ line.methodInfo.account_name }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Bank info — shown for transfer methods without QR -->
                <div v-else-if="hasBankInfo(line.methodInfo)" class="col-12">
                  <div class="card border-0 bg-light rounded-3">
                    <div class="card-body p-3">
                      <div class="small text-muted fw-semibold mb-2">
                        <i class="fa-solid fa-building-columns me-1"/>{{ $t('Bank Account') }}
                      </div>
                      <div v-if="line.methodInfo.bank_name" class="d-flex justify-content-between small mb-1">
                        <span class="text-muted">{{ $t('Bank') }}</span>
                        <span class="fw-semibold">{{ line.methodInfo.bank_name }}</span>
                      </div>
                      <div v-if="line.methodInfo.account_no" class="d-flex justify-content-between small mb-1">
                        <span class="text-muted">{{ $t('Account No') }}</span>
                        <span class="fw-semibold font-monospace">{{ line.methodInfo.account_no }}</span>
                      </div>
                      <div v-if="line.methodInfo.account_name" class="d-flex justify-content-between small">
                        <span class="text-muted">{{ $t('Account Name') }}</span>
                        <span class="fw-semibold">{{ line.methodInfo.account_name }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-5">
                  <label class="form-label small mb-1">{{ $t('Amount') }}</label>
                  <div class="custom-input-group custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
                    <span class="input-group-text bg-white text-muted">฿</span>
                    <input
                      type="number"
                      class="form-control border-start-0 ps-0"
                      step="0.01"
                      min="0"
                      v-model.number="line.amount"
                    >
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="form-label small mb-1">{{ $t('Reference No.') }}</label>
                  <div class="custom-input-group custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :placeholder="$t('Optional')"
                      maxlength="100"
                      v-model.trim="line.reference_no"
                    >
                  </div>
                </div>
                <div class="col-md-3">
                  <label class="form-label small mb-1">{{ $t('Note') }}</label>
                  <div class="custom-input-group custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :placeholder="$t('Optional')"
                      maxlength="255"
                      v-model.trim="line.note"
                    >
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              class="btn btn-sm btn-outline-secondary mb-3"
              @click="addLine"
            >
              <i class="fa-solid fa-plus me-1"/>{{ $t('Add another payment') }}
            </button>

            <div class="card bg-light border-0 rounded-3 mb-3">
              <div class="card-body p-2 small">
                <div class="d-flex justify-content-between">
                  <span class="text-muted">{{ $t('Total Received') }}</span>
                  <span class="fw-bold">{{ $storeIndex.formatThaiCurrency(totalAmount) }}</span>
                </div>
                <div v-if="change >= 0" class="d-flex justify-content-between text-success">
                  <span class="fw-semibold">{{ $t('Change') }}</span>
                  <span class="fw-bold">{{ $storeIndex.formatThaiCurrency(change) }}</span>
                </div>
                <div v-else class="d-flex justify-content-between text-danger">
                  <span class="fw-semibold">{{ $t('Remaining') }}</span>
                  <span class="fw-bold">{{ $storeIndex.formatThaiCurrency(-change) }}</span>
                </div>
              </div>
            </div>
          </template>

          <div class="d-flex gap-2">
            <button
              type="button"
              class="btn btn-outline-secondary flex-grow-1 py-2 fw-bold rounded-3"
              data-bs-dismiss="modal"
            >
              {{ $t('Cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-success flex-grow-1 py-2 fw-bold rounded-3"
              :disabled="!canConfirm || submitting"
              @click="confirm"
            >
              <i v-if="submitting" class="fa-solid fa-spinner fa-spin me-2"/>
              <i v-else class="fa-solid fa-check me-2"/>
              {{ change < 0 ? $t('Partial Payment') : $t('Confirm Payment') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode';
import { generatePromptPayPayload } from '@/utils/promptpay';

const METHOD_ICONS = {
  cash:     'fa-solid fa-money-bill-wave',
  transfer: 'fa-solid fa-qrcode',
  credit:   'fa-solid fa-credit-card',
  card:     'fa-solid fa-credit-card',
  wallet:   'fa-solid fa-wallet',
  voucher:  'fa-solid fa-ticket'
};
const METHOD_COLORS = {
  cash:     'success',
  transfer: 'info',
  credit:   'primary',
  card:     'primary',
  wallet:   'warning',
  voucher:  'secondary'
};

export default {
  name: 'ModalCollectPayment',
  props: {
    bill: { type: Object, required: true },
    paymentMethods: { type: Array, default: () => [] }
  },
  emits: ['close', 'confirm'],
  data() {
    return {
      submitting: false,
      lines: [this.makeBlankLine()],
      qrUrls: [],
      promptPayIcon: require('@/assets/images/icon_promptpay.svg')
    };
  },
  computed: {
    billTotal() {
      return this.toNumber(this.bill.total);
    },
    paidAmount() {
      // Prefer API-supplied paid_amount; fall back to summing payments[]
      if (this.bill.paid_amount != null) return this.toNumber(this.bill.paid_amount);
      return (this.bill.payments || []).reduce((s, p) => s + this.toNumber(p.amount), 0);
    },
    remainingAmount() {
      if (this.bill.remaining_amount != null) return this.toNumber(this.bill.remaining_amount);
      return Math.max(0, this.billTotal - this.paidAmount);
    },
    totalAmount() {
      return this.lines.reduce((s, l) => s + this.toNumber(l.amount), 0);
    },
    change() {
      return this.totalAmount - this.remainingAmount;
    },
    canConfirm() {
      return this.paymentMethods.length > 0
        && this.lines.length > 0
        && this.lines.every(l => l.id && l.payment_method_id && this.toNumber(l.amount) > 0)
        && this.totalAmount > 0;
    },
    // PromptPay payload per line — empty when method has no promptpay_no or amount is 0
    linePayloads() {
      return this.lines.map(l => {
        if (!l.methodInfo || !l.methodInfo.promptpay_no) return '';
        const amt = this.toNumber(l.amount);
        if (amt <= 0) return '';
        return generatePromptPayPayload(
          l.methodInfo.promptpay_no,
          amt,
          l.methodInfo.account_name || '',
          ''
        );
      });
    }
  },
  watch: {
    linePayloads: {
      immediate: true,
      handler(payloads) {
        payloads.forEach((payload, idx) => {
          if (!payload) {
            this.qrUrls[idx] = '';
            return;
          }
          QRCode.toDataURL(
            payload,
            { margin: 1, width: 300, errorCorrectionLevel: 'H' },
            (err, url) => { this.qrUrls[idx] = err ? '' : url; }
          );
        });
        // Drop stale entries when lines shrink
        this.qrUrls = this.qrUrls.slice(0, payloads.length);
      }
    }
  },
  mounted() {
    this.lines[0].amount = this.remainingAmount;
    if (this.paymentMethods.length) {
      this.setMethod(0, this.paymentMethods[0]);
    }
    $(this.$refs.modal).modal('show');
    $(this.$refs.modal).on('hidden.bs.modal', () => this.$emit('close'));
  },
  methods: {
    makeBlankLine() {
      return { id: null, payment_method_id: null, methodInfo: null, amount: 0, reference_no: '', note: '' };
    },
    toNumber(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    },
    methodIcon(type) {
      return METHOD_ICONS[type] || 'fa-solid fa-money-bill';
    },
    methodColor(type) {
      return METHOD_COLORS[type] || 'secondary';
    },
    hasBankInfo(m) {
      return !!(m && (m.bank_name || m.account_no || m.account_name));
    },
    formatPromptPayId(id) {
      const digits = String(id || '').replace(/\D/g, '');
      if (digits.length <= 4) return digits;
      return 'xxx-xxx-' + digits.slice(-4);
    },
    setMethod(idx, m) {
      this.lines[idx].id = m.id;
      this.lines[idx].payment_method_id = m.payment_method_id;
      this.lines[idx].methodInfo = m;
    },
    addLine() {
      const next = this.makeBlankLine();
      if (this.paymentMethods.length) {
        const first = this.paymentMethods[0];
        next.id = first.id;
        next.payment_method_id = first.payment_method_id;
        next.methodInfo = first;
      }
      const remaining = this.remainingAmount - this.totalAmount;
      next.amount = remaining > 0 ? remaining : 0;
      this.lines.push(next);
    },
    removeLine(idx) {
      if (this.lines.length <= 1) return;
      this.lines.splice(idx, 1);
    },
    async confirm() {
      if (!this.canConfirm || this.submitting) return;
      this.submitting = true;
      const payment = this.lines.map(l => ({
        id: l.id,
        payment_method_id: l.payment_method_id,
        amount: this.toNumber(l.amount),
        reference_no: l.reference_no || '',
        note: l.note || ''
      }));
      this.$emit('confirm', { payment });
    },
    hide() {
      $(this.$refs.modal).modal('hide');
    },
    stopSubmitting() {
      this.submitting = false;
    }
  }
};
</script>

<style scoped>
.method-btn {
  text-align: left;
  transition: all 0.15s ease;
}
.method-btn:hover { transform: translateY(-1px); }
</style>
